
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    
import React from "react";
import App from "next/app";
import { SWRConfig } from "swr";
import "@adyen/adyen-web/dist/adyen.css";
import "./styles.css";

import analyticsIDs from "../../conf/lang.analytics";
import gtmIDs from "../../conf/lang.gtm";
import fbIDs from "../../conf/lang.fb";
import popularSearchesFromFile from "../../conf/popularSearches";

import getRoute from "../getRoute";
import getUrl from "../getRoute";

import nextCookie from "next-cookies";
import Router from "next/router";
import Link$ from "next/link";

import "../globals";
import theme from "../theme";

import Root from "@commerce-ui/core2/Root";

import { InjectRouteContext } from "@deosite/lib/RouteContext";
import withCache from "@deosite/containers/Infrastructure/cache/withCache";

import Notification from "@deosite/components-new/Notification";
import SalesBox from "../components/SalesBox";
import MenuDesktopLayout from "../components/MenuDesktop";
import CookieContainer from "../components/CookieContainer";

import { TokenContextProvider } from "@deosite/api/hooks/common/TokenProvider";
import { RecentlyViewedContextProvider } from "@deosite/api/hooks/common/useRecentlyViewedProducts";
import { MinicartContextProvider } from "@deosite/api/hooks/common/useMinicart";
import StoreProviderRedux from "@deosite/app/provider";

import CartProvider from "@deosite/api/hooks/REST/CartProvider";
import { fetcher as categoryTreeFetcher } from "@deosite/api/hooks/REST/useCategoryTree";
import { fetcher as categoriesFetcher } from "@deosite/api/hooks/REST/useCategories";

import { EPointsContextProvider } from "@deosite/api/hooks/REST/EPoints";
import OrdersContextProvider from "@deosite/api/hooks/REST/OrdersProvider";
import { CurrencyContextProvider } from "@deosite/api/hooks/REST/CurrencyProvider";
import { AccountContextProvider } from "@deosite/api/hooks/REST/AccountProvider";
import { WishlistContextProvider } from "@deosite/api/hooks/REST/WishlistProvider";
import { CheckoutRegisterContextProvider } from "@deosite/api/hooks/REST/CheckoutRegisterProvider";

import { fetcher as prismicFetcher } from "@deosite/api/hooks/prismic/usePrismic";
import { fetcher as prismicFooterFetcher } from "@deosite/api/hooks/prismic/usePrismicFooter";
import { fetcher as prismicBannerFetcher } from "@deosite/api/hooks/prismic/usePrismicSaleBanner";
import { fetcher as prismicMenuBannersFetcher } from "@deosite/api/hooks/prismic/usePrismicMenuBanners";
import { fetcher as prismicCategoryBannersFetcher } from "@deosite/api/hooks/prismic/usePrismicCategoryBanners";

import { BackLinkContextProvider } from "@deosite/api/hooks/common/useBackLink";
import { LoaderContextProvider } from "@deosite/api/hooks/common/useLoader";
import { LanguageContextProvider } from "@deosite/api/hooks/common/LanguageProvider";
import { MessageHandlerProvider } from "@deosite/api/hooks/common/MessageHandlerProvider";
import CategoryTreeProvider from "@deosite/api/hooks/common/useCategoryTree";
import { PaginationPaginationLoaderProvider } from "@deosite/api/hooks/common/usePaginationLoader";
import { CookieAgreementsContextProvider } from "@deosite/api/hooks/common/CookieAgreementsProvider";
import { readCacheFile, saveCacheFile } from "@deosite/helpers/cache";
import setSaleBannerVisibility from "@deosite/helpers/setSaleBannerVisibility";
import addAnalyticsAndCommerceScripts from "@deosite/helpers/addAnalyticsAndCommerceScripts";
import getDomainAndLanguage from "@deosite/helpers/getDomainAndLanguage";
import pageRefreshWithTime from "@deosite/helpers/pageRefreshWithTime";
import { NotificationSystemProvider } from "@deosite/api/hooks/common/NotificationSystem";
import { AdvertPopupContextProvider } from "@deosite/api/hooks/common/useAdvertPopup";
import { TIMEOUT } from "@deosite/helpers/prismicErrorHandle";

import ErrorPage from "./_error";
import ErrorDomain from "./_errorDomain";

const Link = props => {
  const { href, route, params, children } = props;
  let url = route ? getRoute(route, params) : { href: href, as: href };
  return (
    <Link$ href={url.href} as={url.as} passHref prefetch={false}>
      {children}
    </Link$>
  );
};

class MyApp extends App {
  state = {};

  static async getInitialProps({ Component, ctx, router }) {
    let pageProps = {};
    let langFromUrl = ctx.cache.get("language", false);

    if (!langFromUrl) {
      langFromUrl = getDomainAndLanguage({
        host: ctx.req.headers.host
      });

      if (langFromUrl?.error) {
        pageProps.error = langFromUrl?.error;
        return { pageProps, errorDomain: true, language: langFromUrl };
      }

      ctx.cache.set("language", langFromUrl);
    }

    let menuBanners = ctx.cache.get("menuBanners", false);
    let footerContent = ctx.cache.get("footerContent", false);
    let saleBanner = ctx.cache.get("saleBanner", false);
    let categoryBanners = ctx.cache.get("categoryBanners", false);
    let menu = ctx.cache.get("menu", false);
    let categories = ctx.cache.get("categories", false);
    let popularSearches = global.__appConfig__.popularSearchesFromJson
      ? popularSearchesFromFile[langFromUrl.language]
      : ctx.cache.get("popularSearches", false);
    let externalContentTimeout = false;

    const cacheFilePath = `${langFromUrl.language}/`;
    const {
      cacheFileExist,
      filenameCategoryCache,
      cachedRequest
    } = await readCacheFile({
      cacheFilePath,
      constantFileName: `menu`,
      contextRequest: ctx.req
    });

    const {
      cacheFileExist: prismicCacheFileExist,
      filenameCategoryCache: prismicFilenameCategoryCache,
      cachedRequest: prismicCachedRequest
    } = await readCacheFile({
      cacheFilePath,
      constantFileName: `prismicContent`,
      contextRequest: ctx.req,
      forPrismic: true
    });

    if (cacheFileExist) {
      [menu, categories] = cachedRequest;

      ctx.cache.set("menu", menu);
      ctx.cache.set("categories", categories);
    }

    if (prismicCacheFileExist) {
      [
        menuBanners,
        footerContent,
        saleBanner,
        categoryBanners,
        popularSearches
      ] = prismicCachedRequest;

      ctx.cache.set("menuBanners", menuBanners);
      ctx.cache.set("footerContent", footerContent);
      ctx.cache.set("saleBanner", saleBanner);
      ctx.cache.set("categoryBanners", categoryBanners);
      ctx.cache.set(
        "popularSearches",
        global.__appConfig__.popularSearchesFromJson
          ? popularSearchesFromFile[langFromUrl.language]
          : popularSearches
      );
    }

    if (!menu) {
      [menu, categories] = await Promise.all([
        categoryTreeFetcher(getUrl, langFromUrl),
        categoriesFetcher(langFromUrl)
      ]);

      ctx.cache.set("menu", menu);
      ctx.cache.set("categories", categories);

      if (cacheFileExist === false) {
        await saveCacheFile({
          cacheFilePath,
          filenameCategoryCache,
          result: [menu, categories]
        });
      }
    }

    if (
      (prismicCacheFileExist === false || prismicCacheFileExist === null) &&
      !footerContent
    ) {
      try {
        [
          menuBanners,
          footerContent,
          saleBanner,
          categoryBanners,
          popularSearches
        ] = await Promise.all([
          prismicMenuBannersFetcher(langFromUrl),
          prismicFooterFetcher("footer", langFromUrl),
          prismicBannerFetcher(
            global.__appConfig__.prismicSaleBannerName,
            langFromUrl
          ),
          prismicCategoryBannersFetcher(langFromUrl),
          global.__appConfig__.popularSearchesFromJson
            ? popularSearchesFromFile[langFromUrl.language]
            : prismicFetcher(
                "popularsearches",
                global.__appConfig__.prismicPopularSearches,
                langFromUrl,
                false
              )
        ]);

        if (
          menuBanners === TIMEOUT ||
          footerContent === TIMEOUT ||
          saleBanner === TIMEOUT ||
          categoryBanners === TIMEOUT
        ) {
          externalContentTimeout = true;
        }

        if (!externalContentTimeout) {
          ctx.cache.set("menuBanners", menuBanners);
          ctx.cache.set("footerContent", footerContent);
          ctx.cache.set("saleBanner", saleBanner);
          ctx.cache.set("categoryBanners", categoryBanners);
          ctx.cache.set("popularSearches", popularSearches);

          if (prismicCacheFileExist === false) {
            await saveCacheFile({
              cacheFilePath,
              filenameCategoryCache: prismicFilenameCategoryCache,
              result: [
                menuBanners,
                footerContent,
                saleBanner,
                categoryBanners,
                popularSearches
              ]
            });
          }
        }
      } catch (err) {
        console.log(`Prismic - app - error while fetching data - ${err}`);
      }
    }

    const cookies = nextCookie(ctx);

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({
        ...ctx,
        cookies,
        Router,
        menu,
        router,
        footerContent,
        categoryBanners,
        saleBanner,
        menuBanners,
        language: langFromUrl,
        categories,
        popularSearches,
        externalContentTimeout
      });
    }

    let showBanner;
    if (!pageProps) {
      showBanner = false;
    } else {
      showBanner = setSaleBannerVisibility(pageProps, saleBanner.saleBanner);
    }

    return {
      cookies,
      pageProps,
      menu,
      footerContent,
      categoryBanners,
      saleBanner,
      menuBanners,
      language: langFromUrl,
      categories,
      popularSearches,
      showBanner,
      externalContentTimeout
    };
  }

  componentDidCatch(error, errorInfo) {
    console.log("CUSTOM ERROR HANDLING", error);
  }

  componentDidMount(prevProps) {
    const scroll = () => {
      window.scrollTo(0, 0);
    };

    if (this.props.cookies?.userAgreementCookie) {
      const agreements = this.props.cookies.userAgreementCookie;
      const currentLanguage = this.props.language.language;

      addAnalyticsAndCommerceScripts(currentLanguage, agreements, {
        analyticsID: analyticsIDs[currentLanguage],
        gtmID: gtmIDs[currentLanguage],
        fbID: fbIDs[currentLanguage]
      });
    }

    pageRefreshWithTime({ router: Router });

    Router.events.on("routeChangeStart", scroll);
    return () => {
      Router.events.off("routeChangeStart", scroll);
    };
  }

  render() {
    const {
      Component,
      pageProps,
      language,
      currency,
      menu,
      externalContentTimeout,
      errorDomain,
      ...props
    } = this.props;

    if (errorDomain || pageProps === undefined) {
      return <ErrorDomain />;
    }

    const { error } = pageProps;

    return (
      <StoreProviderRedux language={language}>
        <LanguageContextProvider language={language}>
          <CurrencyContextProvider
            currencyData={global.__appConfig__.currencyList[language.language]}
          >
            <CategoryTreeProvider tree={menu}>
              <SWRConfig
                value={{
                  revalidateOnFocus: false
                }}
              >
                <TokenContextProvider
                  token={
                    this.props?.cookies[global.__appConfig__.tokenCookieName]
                  }
                >
                  <CookieAgreementsContextProvider
                    analyticsID={analyticsIDs[language.language]}
                    gtmID={gtmIDs[language.language]}
                    fbID={fbIDs[language.language]}
                  >
                    <AdvertPopupContextProvider>
                      <Root theme={theme()}>
                        <NotificationSystemProvider>
                          <MessageHandlerProvider
                            notificationComponent={Notification}
                            errorsPlacement={"bottomLeft"}
                          >
                            <RecentlyViewedContextProvider>
                              <AccountContextProvider>
                                <MinicartContextProvider>
                                  <CartProvider>
                                    <WishlistContextProvider>
                                      <LoaderContextProvider
                                        externalContentTimeout={
                                          externalContentTimeout
                                        }
                                      >
                                        <EPointsContextProvider>
                                          {this.props.cookieNotice}
                                          <InjectRouteContext
                                            Link={Link}
                                            Router={Router}
                                          >
                                            {props.showBanner &&
                                              !externalContentTimeout && (
                                                <SalesBox
                                                  banner={
                                                    props.saleBanner?.saleBanner
                                                      ?.content[0]
                                                  }
                                                  background={
                                                    props.saleBanner.saleBanner
                                                      .background
                                                  }
                                                  minified={true}
                                                />
                                              )}
                                            <BackLinkContextProvider
                                              router={props.router}
                                            >
                                              <OrdersContextProvider>
                                                <CheckoutRegisterContextProvider>
                                                  <CookieContainer />
                                                  <PaginationPaginationLoaderProvider>
                                                    {Component.hasNoMenu && (
                                                      <Component
                                                        {...pageProps}
                                                        {...props}
                                                      />
                                                    )}
                                                    {!Component.hasNoMenu && (
                                                      <MenuDesktopLayout
                                                        menu={menu}
                                                        menuBanners={
                                                          props.menuBanners
                                                        }
                                                        noBorder={
                                                          props.router.route
                                                            .length === 1
                                                            ? true
                                                            : false
                                                        }
                                                        popularSearches={
                                                          props.popularSearches
                                                        }
                                                      >
                                                        {error ? (
                                                          <ErrorPage
                                                            {...pageProps}
                                                            {...props}
                                                            statusCode={
                                                              error.statusCode
                                                                ? error.statusCode
                                                                : 404
                                                            }
                                                          />
                                                        ) : (
                                                          <Component
                                                            {...pageProps}
                                                            {...props}
                                                            categoryBanners={
                                                              props.categoryBanners
                                                            }
                                                          />
                                                        )}
                                                      </MenuDesktopLayout>
                                                    )}
                                                  </PaginationPaginationLoaderProvider>
                                                </CheckoutRegisterContextProvider>
                                              </OrdersContextProvider>
                                            </BackLinkContextProvider>
                                          </InjectRouteContext>
                                        </EPointsContextProvider>
                                      </LoaderContextProvider>
                                    </WishlistContextProvider>
                                  </CartProvider>
                                </MinicartContextProvider>
                              </AccountContextProvider>
                            </RecentlyViewedContextProvider>
                          </MessageHandlerProvider>
                        </NotificationSystemProvider>
                      </Root>
                    </AdvertPopupContextProvider>
                  </CookieAgreementsContextProvider>
                </TokenContextProvider>
              </SWRConfig>
            </CategoryTreeProvider>
          </CurrencyContextProvider>
        </LanguageContextProvider>
      </StoreProviderRedux>
    );
  }
}

const __Page_Next_Translate__ = withCache(MyApp);


    export default __appWithI18n(__Page_Next_Translate__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      
    });
  